import React, { useEffect, useMemo } from 'react';
import { arrayOf, bool, func, node, number, object, oneOf, shape, string } from 'prop-types';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { IconSpinner, ValidationError } from '..';
import css from './FieldCustomSelect.module.css';
import { languageSelectOptions } from '../../marketplace-custom-config';
import { useState } from 'react';
import { oneOfType } from 'prop-types';
import { ReactComponent as ArrowDown } from '../../assets/svg/arrowDown.svg';
import { ReactComponent as ArrowUp } from '../../assets/svg/arrowUp.svg';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useRef } from 'react';

const customLabel = Object.values(languageSelectOptions).reduce((pre, curnt) => {
  pre[curnt.value] = string.isRequired;
  return pre;
}, {});

const delayFunctionCall = (func, delay = 1000) => {
  let timerId = null;
  return function (value) {
    if (timerId) {
      clearTimeout(timerId);
    }
    timerId = setTimeout(async () => {
      clearTimeout(timerId);
      func(value);
    }, delay);
  };
};

const nativeLangOptions = Object.keys(customLabel);

const getDefaultOptionValueForString = (value, options, userNativeLang) => {
  if (typeof value !== 'string') return null;
  const selectedOption = options.find(option => option.value === value);
  if (selectedOption && selectedOption.value && selectedOption.label) {
    return {
      ...selectedOption,
      label:
        typeof selectedOption.label === 'string'
          ? selectedOption.label
          : selectedOption.label[userNativeLang],
    };
  }
  return { value, label: value };
};

const FieldCustomSelectComponent = props => {
  const {
    rootClassName,
    className,
    errorClassName,
    id,
    label,
    input,
    meta,
    onChange,
    options,
    isSearchable,
    userNativeLang,
    placeholder,
    intl,
    noOptionMsgId,
    disabled,
    ignoreFilter,
    clearLanguage,
    valuesFromForm,
    isAsync,
    onFetchOptions,
    showClearBtn,
    clearBtnText,
    ...rest
  } = props;

  const [search, setSearch] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [localOptions, setLocalOptions] = useState(options);
  const [fetchStatus, setFetchStatus] = useState({ isLoading: false, error: null });
  const shouldUseFetchOptions = isAsync && typeof onFetchOptions === 'function';
  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  const { invalid, touched, error } = meta;

  // Error message and input error styles are only shown if the
  // field has been touched and the validation has failed.
  const hasError = touched && invalid && error;

  const filteredOptions = !!ignoreFilter
    ? localOptions
    : useMemo(
      () =>
        localOptions.filter(option =>
          !!search && !shouldUseFetchOptions
            ? (typeof option.label === 'string' ? option.label : option.label[userNativeLang])
              .toLowerCase()
              .includes(search.toLowerCase())
            : true
        ),
      [search, userNativeLang, localOptions]
    );

  const { onChange: inputOnChange, value: inputValue, onBlur } = input;

  const selectedValue = useMemo(
    () =>
      !!inputValue
        ? typeof inputValue === 'string'
          ? getDefaultOptionValueForString(inputValue, filteredOptions, userNativeLang)
          : {
            value: inputValue.value,
            label: !!inputValue.label
              ? typeof inputValue.label === 'string'
                ? inputValue.label
                : inputValue.label[userNativeLang]
              : inputValue.value,
          }
        : null,
    [inputValue, filteredOptions, userNativeLang]
  );

  const toggleOptions = event => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;
    setShowOptions(showOptions => !showOptions);
  };

  const handleOptionClick = (option, event) => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;
    if (typeof onChange === 'function') {
      onChange(option);
    } else {
      inputOnChange(option);
    }

    setShowOptions(false);
  };

  const handlenoOptionClick = event => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleFetchOptions = async value => {
    if (shouldUseFetchOptions) {
      try {
        setFetchStatus({ isLoading: true, error: null });
        const options = await onFetchOptions(value);
        setLocalOptions(options || []);
        setFetchStatus({ isLoading: false });
      } catch (err) {
        setFetchStatus({ isLoading: false, error: err });
      }
    }
  };
  const delayedFetchOption = useMemo(() => delayFunctionCall(handleFetchOptions, 250), []);

  const handleSearchChange = event => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;
    const value = event.target.value;
    setSearch(value);
    if (shouldUseFetchOptions) {
      setFetchStatus({ isLoading: true, error: null });
      delayedFetchOption(value);
    }
  };

  const handleOnBlur = event => {
    if (event.relatedTarget) return;
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;
    onBlur(inputValue);
    setShowOptions(false);
  };

  const handleOnFocus = event => {
    event.preventDefault();
    event.stopPropagation();
    if (disabled) return;
    setShowOptions(true);
  };

  const resetOption = e => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedValue) return;
    setSearch('');
    if (typeof onChange === 'function') {
      onChange(undefined);
    } else {
      inputOnChange(undefined);
    }
    setShowOptions(false);
  };

  const arrowBtnRefCallback = el => {
    if (el) {
    }
  };

  const classes = classNames(rootClassName || css.root, className, {
    [errorClassName]: !!errorClassName && hasError,
    [css.disabledField]: disabled,
  });

  useEffect(() => {
    if (options.length > 0) setLocalOptions(options);
  }, [options]);

  useEffect(() => {
    if (clearLanguage === true) {
      setSearch('');
    }
  }, [clearLanguage]);

  return (
    <div className={classes} tabIndex="0" onBlur={handleOnBlur}>
      {label ? <label htmlFor={id}>{label}</label> : null}
      {isSearchable ? (
        <div className={css.inputWrapper}>
          <input
            id={id}
            value={!!selectedValue && !showOptions ? selectedValue.label : search}
            placeholder={placeholder}
            onChange={handleSearchChange}
            className={css.selectInput}
            onFocus={handleOnFocus}
            disabled={disabled}
          />
          {showClearBtn ? (
            <button
              type="button"
              onClick={resetOption}
              className={classNames(css.clearBtn, { [css.disabledClearBtn]: !selectedValue })}
              disabled={disabled}
            >
              {clearBtnText || <FormattedMessage id="FieldCustomSelect.clearOption" />}
            </button>
          ) : null}

          <button
            type="button"
            onClick={toggleOptions}
            className={classNames(css.arrowBtn, {
              [css.errorArrowBtn]: hasError,
              [css.dividerArrowBtn]: showClearBtn,
            })}
            ref={arrowBtnRefCallback}
            disabled={disabled}
          >
            {showOptions ? <ArrowUp className={css.icon} /> : <ArrowDown className={css.icon} />}
          </button>
        </div>
      ) : (
        <button
          type="button"
          id={id}
          className={classNames(css.selectBtn, { [css.emptySelectBtn]: !selectedValue })}
          onClick={toggleOptions}
          disabled={disabled}
        >
          <span className={css.text}>{!!selectedValue ? selectedValue.label : placeholder}</span>

          {showOptions ? <ArrowUp className={css.icon} /> : <ArrowDown className={css.icon} />}
        </button>
      )}
      {showOptions && !disabled && (
        <div className={css.optionsContainer}>
          <ul className={css.optionsWrapper}>
            {filteredOptions.map((data, i) => {
              const isOptionDisabled =
                valuesFromForm?.jobCategories?.length > 0
                  ? !!valuesFromForm?.jobCategories?.find(val => {
                    return (
                      val.subCategory === data.value &&
                      id.includes('.subCategory') &&
                      val.category ===
                      valuesFromForm?.jobCategories?.[id?.split('[')?.[1][0]]?.category
                    );
                  })
                  : valuesFromForm?.languageRequirements?.length > 0
                    ? !!valuesFromForm?.languageRequirements?.find(val => {
                      return (
                        id.includes('.minimumLevel') &&
                        val.minimumLevel?.value === data.value &&
                        val.language?.value ===
                        valuesFromForm?.languageRequirements?.[id?.split('[')?.[1][0]]?.language
                          ?.value
                      );
                    })
                    : valuesFromForm?.knownLanguages?.length > 0
                      ? !!valuesFromForm?.knownLanguages?.find(val => {
                        return (
                          id.includes('.minimumLevel') &&
                          val.minimumLevel?.value === data.value &&
                          val.language?.value ===
                          valuesFromForm?.knownLanguages?.[id?.split('[')?.[1][0]]?.language?.value
                        );
                      })
                      : false;

              return (
                <li
                  key={data.value}
                  className={classNames({
                    [css.selectedOption]: !!selectedValue && data.value === selectedValue.value,
                    [css.disabledOption]: isOptionDisabled,
                  })}
                >
                  <button
                    type="button"
                    onClick={!isOptionDisabled ? handleOptionClick.bind(null, data) : null}
                    disabled={isOptionDisabled}
                  >
                    <span>
                      {typeof data.label === 'string' ? data.label : data.label[userNativeLang]}
                    </span>
                  </button>
                </li>
              );
            })}
            {filteredOptions.length === 0 && !fetchStatus.isLoading && (
              <li className={css.noSearchOption}>
                <button type="button" onClick={handlenoOptionClick}>
                  {intl.formatMessage({ id: noOptionMsgId })}
                </button>
              </li>
            )}
            {fetchStatus.isLoading ? <IconSpinner /> : null}
          </ul>
        </div>
      )}
      <ValidationError fieldMeta={meta} />
    </div>
  );
};

FieldCustomSelectComponent.defaultProps = {
  rootClassName: null,
  className: null,
  errorClassName: null,
  id: null,
  label: null,
  isSearchable: false,
  userNativeLang: languageSelectOptions.en.value,
  noOptionMsgId: 'FieldCustomSelect.noOption',
  disabled: false,
  isAsync: false,
  showClearBtn: false,
  clearBtnText: null,
};

FieldCustomSelectComponent.propTypes = {
  rootClassName: string,
  className: string,
  errorClassName: string,

  onChange: func,

  // Label is optional, but if it is given, an id is also required so
  // the label can reference the input in the `for` attribute
  id: string,
  label: string,

  // Generated by final-form's Field component
  input: object.isRequired,
  meta: object.isRequired,

  options: arrayOf(
    shape({
      value: string.isRequired,
      label: oneOfType([string, shape(customLabel)]).isRequired,
    })
  ).isRequired,
  isSearchable: bool.isRequired,
  userNativeLang: oneOf(nativeLangOptions).isRequired,
  noOptionMsgId: string.isRequired,
  disabled: bool.isRequired,
  isAsync: bool.isRequired,
  onFetchOptions: func,
  showClearBtn: bool.isRequired,
  clearBtnText: oneOfType([string, node]),
};

const FieldCustomSelectComp = injectIntl(FieldCustomSelectComponent);

const FieldCustomSelect = props => {
  const { reRenderOn = '', ...restProps } = props;
  const lastKey = useRef(0);
  const key = useMemo(() => {
    if (!!lastKey.current) {
      lastKey.current = 0;
    } else {
      lastKey.current = 1;
    }
    return `${props.name}-${lastKey.current}`;
  }, [reRenderOn]);

  return <Field component={FieldCustomSelectComp} {...restProps} key={key} />;
};

FieldCustomSelect.defaultProps = {
  reRenderOn: '',
};

FieldCustomSelect.propTypes = {
  reRenderOn: oneOfType([string, bool, number]).isRequired,
};

export default FieldCustomSelect;